import Constants from '../utils/constants';
import * as Formulas from '../utils/formulas';

const INPUTS = Constants.TOPOLOGY_INPUTS;
const OPTIONS = Constants.TOPOLOGY_OPTIONS;
const SIGNAL_CHAIN_ITEMS = Constants.SIGNAL_CHAIN_ITEMS;
const RESISTORS = Constants.RESISTORS;
const CDP_PARAMETERS = Constants.CDP.PARAMETERS;

const dacGroupA = ['AD5791', 'AD5781'], // Default state: "Non-Inverting amplifier with buffer", "Inverting amplifier with buffer", "Buffer" and "External Reference"
  dacGroupB = ['AD5790', 'AD5780']; // Default state:  "Non-Inverting amplifier without buffer", "Inverting amplifier without buffer", "Buffer" and "External Reference"

// Input disable functions
let disableInput = {
  whenInternalVoltageReference: (params) => {
    if (!(params && params.inputs && params.inputs.length)) return false;
    let voltageReferenceInput = params.inputs.find((input) => input.config.id === INPUTS.VOLTAGE_REFERENCE);
    if (!(voltageReferenceInput && voltageReferenceInput.selectedOption)) return false;
    return voltageReferenceInput.selectedOption.type === OPTIONS.INTERNAL;
  },
  whenDacDoesNotHaveNegativeVrefPin: (params) => {
    if (!(params && params.selectedDac)) return false;
    return !Formulas.GET_CDP_BOOLEAN_VALUE(CDP_PARAMETERS.HAS_VREF_NEGATIVE_PIN, params.selectedDac).value;
  },
};

// Option disable functions
let disableOption = {
  whenSelectedDacIsNotInternal: (params) => {
    if (!params) return true;
    return !Formulas.IS_VALUE_FOUND_IN_CDP_VALUE_LIST(
      CDP_PARAMETERS.VREF_SOURCE,
      params.selectedDac,
      Constants.CDP.INTERNAL,
    );
  },
  whenSelectedDacIsNotExternal: (params) => {
    if (!params) return true;
    return !Formulas.IS_VALUE_FOUND_IN_CDP_VALUE_LIST(
      CDP_PARAMETERS.VREF_SOURCE,
      params.selectedDac,
      Constants.CDP.EXTERNAL,
    );
  },
};

let hideOption = {
  whenSelectedDacIsNotInDacGroup: (dacGroup) => {
    return (params) => {
      if (!params) return true;
      return !dacGroup.includes(params.selectedDac.name.toUpperCase());
    };
  },
  whenSelectedDacIsInDacGroup: (dacGroup) => {
    return (params) => {
      if (!params) return true;
      return dacGroup.includes(params.selectedDac.name.toUpperCase());
    };
  },
};

let topologyInputs = [
  {
    id: INPUTS.MULTIPLYING_DAC,
    name: 'multiplying-dac',
    default: OPTIONS.NO_BUFFER,
    options: [
      {
        id: OPTIONS.BUFFER,
        type: OPTIONS.BUFFER,
        name: 'buffered',
        signalChainItems: [
          SIGNAL_CHAIN_ITEMS.VOLTAGE_REFERENCE,
          SIGNAL_CHAIN_ITEMS.OUTPUT_AMP,
          SIGNAL_CHAIN_ITEMS.BUFFER,
        ],
        resistors: [RESISTORS.OUTPUT],
      },
      {
        id: OPTIONS.NO_BUFFER,
        type: OPTIONS.NO_BUFFER,
        name: 'unbuffered',
        article: 'an',
        signalChainItems: [SIGNAL_CHAIN_ITEMS.VOLTAGE_REFERENCE, SIGNAL_CHAIN_ITEMS.OUTPUT_AMP],
        resistors: [RESISTORS.OUTPUT],
      },
      {
        id: OPTIONS.INVERTING,
        type: OPTIONS.INVERTING,
        name: 'inverting',
        article: 'an',
        signalChainItems: [
          SIGNAL_CHAIN_ITEMS.VOLTAGE_REFERENCE,
          SIGNAL_CHAIN_ITEMS.INVERTING_AMP,
          SIGNAL_CHAIN_ITEMS.OUTPUT_AMP,
        ],
        resistors: [RESISTORS.INVERTING, RESISTORS.OUTPUT],
      },
    ],
  },
  // Iout or Vout pulldown
  {
    id: INPUTS.IOUT_OR_VOUT,
    name: 'iout-or-vout',
    default: OPTIONS.IOUT,
    options: [
      {
        id: OPTIONS.IOUT,
        type: OPTIONS.IOUT,
        name: 'iout',
        // no change?
        signalChainItems: [],
        resistors: [],
      },
      {
        id: OPTIONS.VOUT,
        type: OPTIONS.VOUT,
        name: 'vout',
        signalChainItems: [],
        resistors: [],
      },
    ],
  },
  // External or Internal Rset pulldown
  {
    id: INPUTS.RSET,
    name: 'rset',
    default: OPTIONS.EXTERNAL,
    options: [
      {
        id: OPTIONS.EXTERNAL,
        type: OPTIONS.EXTERNAL,
        name: 'external rset',
        // additional signal chain item to use
        signalChainItems: [SIGNAL_CHAIN_ITEMS.EXTERNAL_RSET],
        resistors: [RESISTORS.EXTERNAL_RSET],
      },
      {
        id: OPTIONS.INTERNAL,
        type: OPTIONS.INTERNAL,
        name: 'internal rset',
        signalChainItems: [],
        resistors: [],
      },
    ],
  },
  {
    id: INPUTS.OUTPUT_RANGE,
    name: 'output-range',
    options: [
      {
        id: OPTIONS.OUTPUT_RANGE,
        type: OPTIONS.OUTPUT_RANGE,
        name: 'output-range',
        associatedData: {
          isInSignalChain: true,
          optionsLocation: 'outputRangeOptions',
          dataLocation: 'outputRange',
          setEntireItem: true,
        },
        signalChainItems: [],
      },
    ],
  },
  {
    id: INPUTS.VOLTAGE_REFERENCE,
    name: 'voltage-reference',
    default: OPTIONS.EXTERNAL,
    options: [
      {
        id: OPTIONS.INTERNAL,
        type: OPTIONS.INTERNAL,
        name: 'internal-reference',
        article: 'an',
        disabledFn: disableOption.whenSelectedDacIsNotInternal,
        associatedData: {
          isInSignalChain: true,
          optionsLocation: 'internalVrefOptions',
          dataLocation: 'internalVref',
        },
        signalChainItems: [SIGNAL_CHAIN_ITEMS.INTERNAL_VOLTAGE_REFERENCE],
      },
      {
        id: OPTIONS.EXTERNAL,
        type: OPTIONS.EXTERNAL,
        name: 'external-reference',
        article: 'an',
        disabledFn: disableOption.whenSelectedDacIsNotExternal,
        //disabledDefault: OPTIONS.INTERNAL,
        signalChainItems: [SIGNAL_CHAIN_ITEMS.VOLTAGE_REFERENCE],
      },
    ],
  },
  {
    id: INPUTS.NON_INVERTING,
    name: 'non-inverting',
    //default: OPTIONS.NON_INVERTING_BUFFER,
    defaultFn: (params) => {
      if (!(params && params.selectedDac)) return OPTIONS.NON_INVERTING_BUFFER;
      else if (dacGroupA.includes(params.selectedDac.name.toUpperCase())) return OPTIONS.NON_INVERTING_BUFFER;
      else if (dacGroupB.includes(params.selectedDac.name.toUpperCase())) return OPTIONS.NON_INVERTING_NO_BUFFER;
      else if (Formulas.GET_CDP_BOOLEAN_VALUE(CDP_PARAMETERS.HAS_VREF_POS_INTERNAL_BUFFER, params.selectedDac).value)
        return OPTIONS.NO_BUFFER;
      else return OPTIONS.BUFFER;
    },
    disabledValue: OPTIONS.NO_BUFFER,
    disabledFn: disableInput.whenInternalVoltageReference,
    options: [
      {
        id: OPTIONS.NON_INVERTING_BUFFER,
        type: OPTIONS.NON_INVERTING_BUFFER,
        name: 'non-inverting-buffer',
        hiddenFn: hideOption.whenSelectedDacIsNotInDacGroup(dacGroupA),
        signalChainItems: [SIGNAL_CHAIN_ITEMS.NON_INVERTING_AMP, SIGNAL_CHAIN_ITEMS.NON_INVERTING_BUFFER],
        resistors: [RESISTORS.NON_INVERTING],
      },
      {
        id: OPTIONS.NON_INVERTING_NO_BUFFER,
        type: OPTIONS.NON_INVERTING_NO_BUFFER,
        name: 'non-inverting-no-buffer',
        hiddenFn: hideOption.whenSelectedDacIsInDacGroup(dacGroupA),
        signalChainItems: [SIGNAL_CHAIN_ITEMS.NON_INVERTING_AMP],
        resistors: [RESISTORS.NON_INVERTING],
      },
      {
        id: OPTIONS.BUFFER,
        type: OPTIONS.BUFFER,
        name: 'buffer',
        hiddenFn: hideOption.whenSelectedDacIsInDacGroup(dacGroupB),
        signalChainItems: [SIGNAL_CHAIN_ITEMS.NON_INVERTING_BUFFER],
      },
      {
        id: OPTIONS.NO_BUFFER,
        type: OPTIONS.NO_BUFFER,
        name: 'no-buffer',
        hiddenFn: hideOption.whenSelectedDacIsInDacGroup(dacGroupA),
        signalChainItems: [],
      },
    ],
  },
  {
    id: INPUTS.INVERTING,
    name: 'inverting',
    article: 'an',
    //default: OPTIONS.BUFFER,
    // this should happen automatically but it doesn't hurt to be explicit
    defaultFn: (params) => {
      if (!(params && params.selectedDac)) return OPTIONS.BUFFER;
      else if (dacGroupA.includes(params.selectedDac.name.toUpperCase())) return OPTIONS.BUFFER;
      else if (dacGroupB.includes(params.selectedDac.name.toUpperCase())) return OPTIONS.NO_BUFFER;
      else return OPTIONS.BUFFER;
    },
    disabledValue: OPTIONS.NONE,
    disabledFn: (params) => {
      let result = disableInput.whenInternalVoltageReference(params);
      if (!result) result = disableInput.whenDacDoesNotHaveNegativeVrefPin(params);
      return result;
    },
    options: [
      {
        id: OPTIONS.BUFFER,
        type: OPTIONS.BUFFER,
        name: 'inverting-buffer',
        hiddenFn: hideOption.whenSelectedDacIsNotInDacGroup(dacGroupA),
        signalChainItems: [SIGNAL_CHAIN_ITEMS.INVERTING_AMP, SIGNAL_CHAIN_ITEMS.INVERTING_BUFFER],
        resistors: [RESISTORS.INVERTING],
      },
      {
        id: OPTIONS.NO_BUFFER,
        type: OPTIONS.NO_BUFFER,
        name: 'inverting-no-buffer',
        hiddenFn: hideOption.whenSelectedDacIsInDacGroup(dacGroupA),
        signalChainItems: [SIGNAL_CHAIN_ITEMS.INVERTING_AMP],
        resistors: [RESISTORS.INVERTING],
      },
      {
        id: OPTIONS.NONE,
        type: OPTIONS.NONE,
        name: 'no-negative-reference',
        signalChainItems: [],
      },
    ],
  },
  {
    id: INPUTS.OUTPUT,
    name: 'output',
    article: 'an',
    // default: OPTIONS.BUFFER,
    defaultFn: (params) => {
      if (!(params && params.selectedDac)) return OPTIONS.BUFFER;
      else if (dacGroupA.includes(params.selectedDac.name.toUpperCase())) return OPTIONS.BUFFER;
      else if (dacGroupB.includes(params.selectedDac.name.toUpperCase())) return OPTIONS.BUFFER;
      else if (Formulas.GET_CDP_BOOLEAN_VALUE(CDP_PARAMETERS.HAS_INTERNAL_OUTPUT_AMPLIFIER, params.selectedDac).value)
        return OPTIONS.NONE;
      else return OPTIONS.BUFFER;
    },
    options: [
      {
        id: OPTIONS.BUFFER,
        type: OPTIONS.BUFFER,
        name: 'buffer',
        signalChainItems: [SIGNAL_CHAIN_ITEMS.OUTPUT_BUFFER],
      },
      {
        id: OPTIONS.EXTERNAL_RESISTORS,
        type: OPTIONS.EXTERNAL_RESISTORS,
        name: 'output-amplifier-ext-resistors',
        hiddenFn: hideOption.whenSelectedDacIsInDacGroup(dacGroupA),
        signalChainItems: [SIGNAL_CHAIN_ITEMS.OUTPUT_AMP],
        resistors: [RESISTORS.OUTPUT],
      },
      {
        id: OPTIONS.NONE,
        type: OPTIONS.NONE,
        name: 'no-output-amplifier',
        hiddenFn: hideOption.whenSelectedDacIsInDacGroup(dacGroupA),
        signalChainItems: [],
      },
    ],
  },
  {
    id: INPUTS.FP_NON_INVERTING,
    name: 'fp-non-inverting',
    //default: OPTIONS.NON_INVERTING_BUFFER,
    defaultFn: () => {
      return OPTIONS.BUFFER;
    },
    disabledValue: OPTIONS.NO_BUFFER,
    disabledFn: disableInput.whenInternalVoltageReference,
    options: [
      {
        id: OPTIONS.NON_INVERTING_NO_BUFFER,
        type: OPTIONS.NON_INVERTING_NO_BUFFER,
        name: 'non-inverting-no-buffer',
        signalChainItems: [SIGNAL_CHAIN_ITEMS.NON_INVERTING_AMP],
        resistors: [RESISTORS.NON_INVERTING],
      },
      {
        id: OPTIONS.BUFFER,
        type: OPTIONS.BUFFER,
        name: 'buffer',
        signalChainItems: [SIGNAL_CHAIN_ITEMS.NON_INVERTING_BUFFER],
      },
      {
        id: OPTIONS.NO_BUFFER,
        type: OPTIONS.NO_BUFFER,
        name: 'no-buffer',
        signalChainItems: [],
      },
    ],
  },
  {
    id: INPUTS.FP_DAC_TYPE,
    name: 'fp-dac-type',
    isShown: false,
    defaultFn: (params) => {
      let dacType = params.selectedDac[CDP_PARAMETERS.DAC_TYPE];
      if (dacType.value.includes('Voltage Out')) {
        return OPTIONS.DAC_TYPE_VOLTAGE_OUT;
      }
      return OPTIONS.DAC_TYPE_CURRENT_OUT;
    },
    options: [
      {
        id: OPTIONS.DAC_TYPE_CURRENT_OUT,
        type: OPTIONS.DAC_TYPE_CURRENT_OUT,
        name: 'current',
        // no change?
        signalChainItems: [],
        resistors: [],
      },
      {
        id: OPTIONS.DAC_TYPE_VOLTAGE_OUT,
        type: OPTIONS.DAC_TYPE_VOLTAGE_OUT,
        name: 'voltage',
        signalChainItems: [],
        resistors: [],
      },
    ],
  },
  {
    id: INPUTS.FP_DAC_OUTPUT,
    name: 'fp-dac-output',
    defaultFn: (params) => {
      let dacType = params.selectedDac[CDP_PARAMETERS.DAC_TYPE];
      if (dacType.value.includes('Voltage Out')) {
        return OPTIONS.NONE;
      }
      return OPTIONS.BUFFER;
    },
    options: [
      {
        id: OPTIONS.BUFFER,
        type: OPTIONS.BUFFER,
        name: 'buffer',
        signalChainItems: [SIGNAL_CHAIN_ITEMS.OUTPUT_BUFFER],
      },
      {
        id: OPTIONS.NONE,
        type: OPTIONS.NONE,
        name: 'no-output-amplifier',
        hiddenFn: (params) => {
          let dacType = params.selectedDac[CDP_PARAMETERS.DAC_TYPE];
          return dacType.value.includes('Current Steering') || dacType.value.includes('Current Out');
        },
        signalChainItems: [],
      },
    ],
  },
];

export default {
  topologyInputs: topologyInputs,
};
